import { useQuery } from "@apollo/client";
import { PRODUCTS } from "modules/custom/api";
import CustomLibraryProductLink from "modules/custom/CustomLibraryProductLink";
import { sortDirectionMap } from "constants/sortDirectionMap";
import {
  OrganizationProductType,
  QueryGetProductsOrderByColumn,
  SortOrder,
} from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import ProductActionsCell from "modules/products/ProductActionsCell/ProductActionsCell";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getRoute } from "utils/routing";
import useGetProducts from "./useGetProducts";

const productsInitialSortDirectionValue = SortOrder.ASC;

export default function useProductsTable({
  productTypeId,
}: {
  productTypeId: OrganizationProductType["id"];
}) {
  const { filtering, offset, sorting } = usePaginatedVariables();
  const custom = useQuery(PRODUCTS, { fetchPolicy: "network-only" });
  const {
    data: production,
    loading: productionLoading,
    error: productionError,
    paginatorInfo: productionPages,
    getAllData,
  } = useGetProducts({
    page: 1,
    first: 50,
    search: filtering.debouncedSearchTerm,
    orderBy: [
      {
        column: QueryGetProductsOrderByColumn.NAME,
        order: sortDirectionMap[sorting.sortDirection] || productsInitialSortDirectionValue,
      },
    ],
    organizationProductTypeId: productTypeId,
  });
  useEffect(() => {
    if (!productionLoading && productionPages?.total > (production?.length ?? 0)) getAllData();
  }, [productionLoading, productionPages?.total, production?.length, getAllData]);
  const rows = useMemo(
    () =>
      [
        ...(production ?? []),
        ...(custom.data?.customProducts ?? [])
          .filter(
            (p: any) =>
              p.organizationProductType.id === productTypeId &&
              [p.definition.internalName, p.definition.name]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes((filtering.debouncedSearchTerm ?? "").toLowerCase())
          )
          .map((p: any) => ({
            ...p,
            id: `custom:${p.id}`,
            customProduct: p,
            name: p.definition.internalName || p.definition.name,
          })),
      ].sort((a, b) => a.name.localeCompare(b.name) * (sorting.sortDirection === "desc" ? -1 : 1)),
    [production, custom.data, productTypeId, filtering.debouncedSearchTerm, sorting.sortDirection]
  );
  const total = rows.length;
  const currentPage = Math.min(offset.page, Math.max(1, Math.ceil(total / offset.first)));
  const data = rows.slice((currentPage - 1) * offset.first, currentPage * offset.first);
  const paginatorInfo = {
    currentPage,
    perPage: offset.first,
    total,
    lastPage: Math.max(1, Math.ceil(total / offset.first)),
    hasMorePages: currentPage * offset.first < total,
    count: data.length,
    firstItem: total ? (currentPage - 1) * offset.first + 1 : 0,
    lastItem: Math.min(currentPage * offset.first, total),
  };
  const loading =
    productionLoading ||
    custom.loading ||
    (productionPages?.total ?? 0) > (production?.length ?? 0);
  const error = productionError || custom.error;
  const pagination = usePaginatedResources({
    paginatorInfo,
    paginate: offset.paginate,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Product Name",
        accessor: "name",
        width: "80%",
        Cell: ({ value, row }) =>
          row.original.customProduct ? (
            <CustomLibraryProductLink product={row.original.customProduct} />
          ) : (
            <Link
              style={{ color: "inherit" }}
              to={getRoute("product-types.products.update", [
                ["productTypeId", productTypeId],
                ["productId", row.values.id],
              ])}
            >
              {row.original.subcontracted && `[Subcontracted]`}{" "}
              {row.original.unitPriceProduct && `[Unit Priced]`} {value}
            </Link>
          ),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: (props) =>
          props.row.original.customProduct ? (
            <CustomLibraryProductLink product={props.row.original.customProduct} actions />
          ) : (
            <ProductActionsCell {...props} />
          ),
      },
    ];
  }, [productTypeId]);

  const tableData = useMemo(() => {
    return { columns, rows: data };
  }, [columns, data]);

  return {
    data,
    loading,
    error,
    columns,
    tableData,
    pagination,
    filtering,
    paginatorInfo,
  } as const;
}
